import type { FormEvent } from 'react'
import { useState } from 'react'

import { useForgotPassword } from '@refinedev/core'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { AuthCard } from '~/components/refineUi/form/authCard'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { cn } from '~/lib/utils'
import type { ForgotPasswordParams } from '~/providers/authProvider'
import { LOGIN_PATH } from '~/providers/authProvider'

/**
 * "Forgot password?": asks the backend to mail a reset link. The answer is
 * the same whether the email has an account or not (no user enumeration).
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const backToSignIn = (
    <Link
      to={LOGIN_PATH}
      className={cn('text-sm', 'text-blue-600', 'dark:text-blue-400')}
    >
      {t('auth.forgotPassword.backToSignIn')}
    </Link>
  )
  const [email, setEmail] = useState('')
  const {
    mutate: requestReset,
    isPending,
    data,
  } = useForgotPassword<ForgotPasswordParams>()

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    requestReset({ email })
  }

  if (data?.success === true) {
    return (
      <AuthCard
        title={t('auth.forgotPassword.sent.title')}
        description={t('auth.forgotPassword.sent.description')}
      >
        <p
          role="status"
          className={cn('text-sm', 'mt-6')}
        >
          <Trans
            i18nKey="auth.forgotPassword.sent.notice"
            values={{ email }}
            components={[<strong key="email" />]}
          />
        </p>
        <div className={cn('mt-6')}>{backToSignIn}</div>
      </AuthCard>
    )
  }

  return (
    <AuthCard
      title={t('auth.forgotPassword.title')}
      description={t('auth.forgotPassword.description')}
    >
      <form onSubmit={handleSubmit}>
        <div className={cn('flex', 'flex-col', 'gap-2', 'mt-6')}>
          <Label htmlFor="email">{t('auth.forgotPassword.email')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <Button
          type="submit"
          size="lg"
          className={cn('w-full', 'mt-6')}
          disabled={isPending}
        >
          {t('auth.forgotPassword.submit')}
        </Button>
      </form>
      <div className={cn('mt-6')}>{backToSignIn}</div>
    </AuthCard>
  )
}
